// AppShell — sidebar + topbar (notifications, user menu, app switcher) + footer.
// Mirrors review/commerce-app/layout.jsx. Pages render as children.
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/components/AppShell.tsx. Every departure from that
// file is marked ADAPTED and explains itself; the rest is the handoff as
// delivered, so a reissued prototype can be re-ported instead of merged.
//
// ADAPTED (throughout): design-system button handlers are `onPress`, the React
// Aria event the DS Button is built on, which fires for pointer, keyboard, and
// touch alike. Plain DOM elements keep `onClick`.
//
// ADAPTED (whole file): design-system components are imported by module path
// rather than from the `@miq/design-system` barrel. The barrel re-exports every
// component in the library, including ones whose modules import chart, carousel,
// form, and toast packages this app does not install — and five of them import
// version-pinned specifiers (`recharts@2.15.2`, `embla-carousel-react@8.6.0`, …)
// that no bundler resolves. Importing by path keeps the dependency surface to
// what this screen actually renders, and still fails the build loudly when
// vendor/design-system is absent, which is the guarantee this phase exists to
// prove.
import { useState, type ReactNode } from "react";
import {
  Home, FileText, Server, Gauge, Receipt, Users, ShoppingCart, ExternalLink,
  ChevronDown, ChevronLeft, LayoutGrid, Settings, Lock, LogOut, Building2, ScrollText, BellRing, HardDriveDownload,
  ArrowLeftRight, Coins, Banknote,
  type LucideIcon,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@miq/design-system/components/ui/avatar";
import { buttonVariants } from "@miq/design-system/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@miq/design-system/components/ui/dropdown-menu";
import { soleChannelConsole, usePacks } from "../data/catalogue";
import type { PlaceToAct } from "../auth/api";
import { LEGAL } from "../legal";

// ADDED (M-W35-25): `subscriptions`. The handoff's nav has five entries and its
// Home dashboard is the only way into a subscription — but the Django templates
// this portal has to reach parity with have a "what you hold" page, and the
// three flows that hang off it (status, deployment request, cancellation) have
// no other entry point. So the nav gains a sixth line rather than those
// journeys being unreachable. Flagged for the async design pass: a sixth
// sidebar entry is Carla's call to confirm, and the alternative is a link from
// Home, which belongs to another phase.
//
// ADDED (M-W35-36): a seventh and eighth entry, All Customers and See-All
// Audit — and unlike the sixth, they do not lengthen anybody's sidebar. The
// handoff drew ONE nav; there are now two, and which one is drawn follows the
// shape of the identity the session carries. A customer sees exactly the six
// above and never a metricsIQ entry; a metricsIQ person acting across the
// fleet sees exactly the two below and none of the six, because every one of
// the six is a customer endpoint that answers their token 401. So the eight
// entries are two navs of six and two rather than a list of eight, and no
// person is offered a line that cannot be opened. Also for the design pass:
// the metricsIQ sidebar is a second surface Carla has not seen.
export type CmRoute =
  | "home"
  | "subscriptions"
  | "fleet"
  | "metering"
  | "billing"
  | "users"
  | "metricsiq-customers"
  | "metricsiq-deployments"
  | "metricsiq-alerts"
  | "metricsiq-fleet-costs"
  | "metricsiq-fleet-revenue"
  | "metricsiq-audit";

// ADAPTED: the handoff points at `/assets/mDecide-logo.svg` and asks the host
// app to substitute its own hosted paths. Brand lockups are images, not tokens,
// so the dark surface needs its own file rather than a re-tokenised one — hence
// a pair per mark. Both files are copies of the design system's own
// assets/logos/, kept under their DS filenames; refresh them from
// frontend/vendor/design-system/assets/logos/ when the DS reissues a lockup.
const LOGO = {
  mDecide: {
    light: "/assets/mDecide-White-Background-v3.svg",
    dark: "/assets/mDecide-Dark-Background-v3.svg",
  },
  metricsIQ: {
    light: "/assets/metricsIQ-White-Background-v3.svg",
    dark: "/assets/metricsIQ-Dark-Background-v3.svg",
  },
};

type NavEntry = { id: CmRoute; label: string; icon: LucideIcon };

/** What somebody acting for one customer is offered. The handoff's nav. */
const CUSTOMER_NAV: NavEntry[] = [
  { id: "home", label: "Home", icon: Home },
  { id: "subscriptions", label: "Subscriptions", icon: FileText },
  { id: "fleet", label: "Fleet Management", icon: Server },
  { id: "metering", label: "Metering", icon: Gauge },
  { id: "billing", label: "Billing", icon: Receipt },
  { id: "users", label: "Users", icon: Users },
];

/**
 * What a metricsIQ person acting across the fleet is offered.
 *
 * **"Fleet Management" above is untouched and this list deliberately borrows
 * nothing from it.** That entry is a live customer-facing screen showing that
 * customer's own instances; these two show every customer's. The two meanings
 * of the word collided, and the collision is resolved by naming the new
 * screens for what they show rather than by renaming a screen customers are
 * already using.
 *
 * The drill-in — one customer, seen from outside — has no entry here on
 * purpose: it is reached by choosing a customer on All Customers, so the
 * sidebar cannot offer a link to a customer nobody has picked yet.
 */
const STAFF_NAV: NavEntry[] = [
  { id: "metricsiq-customers", label: "All Customers", icon: Building2 },
  // Its own screen, not a banner on All Customers. An unresolved
  // silence alert is raised once per instance per hour, so the count
  // grows without bound while nobody acts on it — as a card above the
  // customer list it pushed the fleet off the page within a day. A
  // screen you choose to open shows the same rows without deciding
  // that they are the most important thing on somebody else's.
  {
    id: "metricsiq-deployments",
    label: "Deployment Requests",
    icon: HardDriveDownload,
  },
  { id: "metricsiq-alerts", label: "Metering Alerts", icon: BellRing },
  // What each instance cost metricsIQ to run, as the instance posts it
  // day by day. Our own book, so it is on this nav and never on the
  // customer's: a customer's Fleet Management shows what they used, and
  // what running their instance cost us is not a figure they are shown.
  { id: "metricsiq-fleet-costs", label: "Fleet Costs", icon: Coins },
  // What each customer is billed over the same range, beside what their
  // instances cost — the other half of the margin, right after the cost
  // half. Billing and Metering show one customer their own figures; this
  // shows a metricsIQ person everybody's, and so lives on this nav only.
  { id: "metricsiq-fleet-revenue", label: "Fleet Revenue", icon: Banknote },
  { id: "metricsiq-audit", label: "See-All Audit", icon: ScrollText },
];

/**
 * How a granted role reads, and the fallback is the codename itself.
 *
 * The wire carries `see_all_customers`, which is a permission's name and not a
 * sentence anybody says. Translated here for the same reason instance states
 * are translated on FleetPage: the endpoint reports the service's own word and
 * what a word should LOOK like is the screen's business. A role this table has
 * not met yet shows as it arrived, which is wrong-looking rather than absent —
 * the failure mode that gets noticed and fixed.
 */
const ROLE_LABEL: Record<string, string> = {
  see_all_customers: "Every customer",
};

/**
 * Who is signed in, answered by the backend.
 *
 * A sum, mirroring the one GET /portal/api/me/ answers in — see
 * `PortalIdentity` in auth/api.ts. The shell asks the same question the wire
 * answers, discriminating on the presence of `customerName`, rather than
 * taking a nullable name and having to decide at four render sites what a
 * missing customer means. A metricsIQ person acts for no customer, so there is
 * no name to be missing: what there is instead is the role they act under.
 *
 * ADDED (M-W35-36 T5): `elsewhere`, on BOTH arms and required on both. It is
 * everywhere this person may act except where they already are — see
 * `placesToActBesidesHere` in auth/api.ts, which is where the subtraction
 * happens. Required rather than optional because an absent list and an empty
 * one would be two spellings of "nowhere else to go", and the render would
 * then have to know which of the two it was looking at. Empty is the honest
 * spelling for a person with nowhere else to act.
 */
export type ShellIdentity =
  | { email: string; customerName: string; elsewhere: PlaceToAct[] }
  | { email: string; role: string; elsewhere: PlaceToAct[] };

export interface AppShellProps {
  active: CmRoute;
  onNavigate: (route: CmRoute) => void;
  /**
   * Open the account-settings modal on one of its tabs.
   *
   * The two named here are the two the modal has. It used to take four and
   * fold the other two onto Profile, which is what a menu entry for
   * notifications needed; that entry is gone, so the type can say what is
   * true instead of absorbing a tab nothing opens.
   */
  onOpenAccount: (tab: "profile" | "security") => void;
  onSignOut: () => void;
  /**
   * Move this session somewhere else its person may act.
   *
   * Required, not optional, and it is the same argument `onSignOut` is
   * required by: the shell offers the action, so the shell may not be handed a
   * version of itself where pressing it does nothing. The control is drawn
   * only where `identity.elsewhere` is non-empty, so a shell rendered for a
   * person with nowhere else to act never reaches it — but the type still has
   * to say what would happen if it did.
   */
  onSwitchTo: (place: PlaceToAct) => void;
  /**
   * ADAPTED: the handoff draws the masthead and the user menu from the drop's
   * CM_ACCOUNT fixture. Phase 1 has a real session, and a fixture name beside a
   * real one is not a placeholder any more — it is the wrong person's name on
   * the screen. So the identity lines come from GET /portal/api/me/.
   *
   * THE AWS ACCOUNT LINE IS GONE (2026-09-01), not deferred. It could never be
   * filled: nothing in this service records an account number for a payer —
   * that is the channel's identifier for them, not ours — so it was the
   * fixture's invented "AWS 8842-1156-0093" for every customer, forever, under
   * their real name. HomePage.tsx had already reached this conclusion for the
   * page eyebrow and replaced it with the customer; the masthead was missed.
   * A line that can only ever be false is not a placeholder.
   *
   * REQUIRED (M-W38-02). It was optional so the shell would render on the
   * drop's fixtures for review, and the absent case fell back to the fixture
   * company's name, address and initials — another customer's identity under a
   * real customer's masthead, latent only for as long as every caller
   * remembered to pass the prop. The shell now asks for the session it draws,
   * and the fixture module it fell back to is gone from the bundle with it.
   */
  identity: ShellIdentity;
  children: ReactNode;
}

/**
 * Two letters for the avatar, taken from the address.
 *
 * ADAPTED: the fixture ships `initials`. A real account here is an email and a
 * password — this pool stores no first or last name — so there is no name to
 * take initials from, and the address is the honest source.
 */
function initialsOf(email: string): string {
  return email.slice(0, 2).toUpperCase();
}

/** A brand mark and its dark-surface twin; the active theme shows one. */
function Logo({ mark, alt, className }: { mark: keyof typeof LOGO; alt: string; className: string }) {
  return (
    <>
      <img src={LOGO[mark].light} alt={alt} className={`${className} dark:hidden`} />
      <img src={LOGO[mark].dark} alt={alt} className={`hidden ${className} dark:block`} />
    </>
  );
}

/**
 * The sidebar way out to the marketplace this customer bought through.
 *
 * It was a <Button> with a label, an external-link glyph and no handler at
 * all, drawn for every customer whatever channel they arrived on — so it
 * promised "Subscribe, upgrade, and pay in AWS Marketplace" and did nothing,
 * to people who may never have seen AWS. Now it is an anchor to the console
 * the subscription is actually managed in, and it is drawn only where the
 * customer holds exactly one channel that has such a console
 * (`soleChannelConsole`). A customer on no marketplace gets no entry rather
 * than a link to somewhere they have no account.
 *
 * Its own component because the read has to be, not for tidiness: this sits
 * inside the customer half of the shell, and `usePacks` reads a customer
 * endpoint that answers a metricsIQ token 401. A hook in the shell body would
 * run for both shells; a hook in a component the shell mounts only for
 * customers runs for the shell that may ask.
 */
function MarketplaceConsoleLink() {
  const packs = usePacks();
  const marketplace =
    packs.status === "ready" ? soleChannelConsole(packs.data.packs) : null;

  if (!marketplace) {
    return null;
  }

  return (
    <div className="mt-auto border-t p-2">
      {/* ADAPTED: the DS Button takes React Aria's prop set, which has no
          `title`, so the handoff's hover hints move to a wrapping element —
          and the control itself is an anchor, so it wears the DS ghost recipe
          rather than being a Button that would have to fake navigation. */}
      <span className="block" title={`Subscribe, upgrade, and pay in ${marketplace.name}`}>
        <a
          href={marketplace.url}
          target="_blank"
          rel="noreferrer"
          className={buttonVariants({ variant: "ghost", className: "w-full justify-start gap-2.5" })}
        >
          <ShoppingCart className="size-4" /> {marketplace.name}{" "}
          <ExternalLink className="ml-auto size-3 opacity-60" />
        </a>
      </span>
    </div>
  );
}

/**
 * How the fleet-wide destination reads in the switcher.
 *
 * Not `ROLE_LABEL`'s wording, deliberately. That table translates a grant into
 * what somebody IS ("Every customer" under their address); this is a place to
 * GO, and it is named for the screen it lands on — the same words the sidebar
 * entry uses, so pressing it and finding "All Customers" is not a surprise.
 */
const ACROSS_ALL_CUSTOMERS = "All Customers";

/**
 * The one control this task adds: where else this person may act.
 *
 * It sits beside the masthead's identity lines rather than in the sidebar,
 * because it changes WHO the whole surface belongs to and not which of that
 * surface's screens is showing — the sidebar is a list of the second kind. The
 * account menu was the alternative and is where it lands for the person who
 * has one: a metricsIQ person's menu holds only Sign out, so a switcher buried
 * in it would be a switcher nobody found.
 *
 * Drawn only when `places` is non-empty, which is exactly "there is more than
 * one place to act". Somebody who belongs to one customer and holds no role
 * sees no control at all, and the shell is what it was for them.
 */
function ScopeSwitcher({
  places,
  onSwitchTo,
}: {
  places: PlaceToAct[];
  onSwitchTo: (place: PlaceToAct) => void;
}) {
  return (
    <DropdownMenu>
      {/* ADAPTED: as with the other two triggers in this file, the DS
          DropdownMenuTrigger renders its own button, so the ghost styling
          comes from the `buttonVariants` recipe rather than a nested Button. */}
      <DropdownMenuTrigger
        className={buttonVariants({ variant: "ghost", size: "sm", className: "gap-1.5 px-2" })}
        aria-label="Act somewhere else"
      >
        <ArrowLeftRight className="size-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Switch</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Act for
        </DropdownMenuLabel>
        {places.map((place) => (
          <DropdownMenuItem
            key={"customer" in place ? place.customer.id : "across-all-customers"}
            onAction={() => onSwitchTo(place)}
          >
            {"customer" in place ? (
              <>
                <Building2 className="size-4" /> {place.customer.name}
              </>
            ) : (
              <>
                <LayoutGrid className="size-4" /> {ACROSS_ALL_CUSTOMERS}
              </>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Where the left-nav collapse choice is remembered. */
const NAV_COLLAPSED_KEY = "miq-commerce-nav";

function readNavCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === "collapsed";
  } catch {
    return false;
  }
}

export function AppShell({
  active,
  onNavigate,
  onOpenAccount,
  onSignOut,
  onSwitchTo,
  identity,
  children,
}: AppShellProps) {
  // Which of the two shells this is. Asked once, here, so that the sidebar,
  // the masthead and the user menu cannot end up disagreeing about who is
  // looking at them — which is the failure a per-site check invites.
  const actsAcrossTheFleet = "role" in identity;
  // `actsFor` is the line under the address: which customer this session acts
  // for, or, for somebody who acts for none, the role they act under. It is
  // not a name in the second case and does not pretend to be one; a blank
  // there would read as a customer whose name failed to load, which is the
  // same reason the backend omits the key rather than sending an empty block.
  const actsFor = "role" in identity
    ? (ROLE_LABEL[identity.role] ?? identity.role)
    : identity.customerName;
  // Whose surface this is, in the masthead. For a customer it is their own
  // name; for a metricsIQ person it is metricsIQ, because the screens they are
  // looking at are ours and belong to no customer on them.
  const surface = actsAcrossTheFleet ? "metricsIQ" : actsFor;
  // Where else they could be. Empty where there is nowhere: one customer and
  // no role is one place to act, and the switcher is not drawn.
  const elsewhere = identity.elsewhere;
  const signedInAs = identity.email;
  const avatar = initialsOf(identity.email);
  // The left nav collapses to an icon rail, as in the platform and the
  // workspace; the choice outlives the visit.
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);
  const toggleNav = () =>
    setNavCollapsed((was) => {
      const next = !was;
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, next ? "collapsed" : "open");
      } catch {
        // A browser that refuses storage loses the preference, not the nav.
      }
      return next;
    });
  return (
    <div className="flex min-h-screen bg-background">
      {/* STYLE-RULES 5: the Portfolio nav is the pattern for every app —
          288px, white, the lockup in a 64px header above a hairline that
          meets the top bar's, and no section eyebrow. */}
      <aside
        className={`relative flex ${navCollapsed ? "w-[60px]" : "w-72"} shrink-0 flex-col border-r bg-card`}
        data-state={navCollapsed ? "collapsed" : "expanded"}
      >
        {/* The collapse control sits on the nav's right border beside the
            menu, not by the logo (Owner, Sidebar_example; .ds-sidebar-toggle). */}
        <button
          type="button"
          className="ds-sidebar-toggle"
          aria-label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
          aria-expanded={!navCollapsed}
          onClick={toggleNav}
          style={{ top: 78 }}
        >
          <ChevronLeft />
        </button>
        <div className={`flex min-h-16 items-center border-b border-border py-2 ${navCollapsed ? "justify-center px-2" : "px-4"}`}>
          {/* Collapsed, the lockup is cropped to its round mark. */}
          <div className={navCollapsed ? "w-[30px] overflow-hidden" : undefined}>
            <Logo mark="mDecide" alt="mDecide" className="h-9 max-w-none" />
          </div>
        </div>
        {/* The design-system nav recipe, the same one the platform shell uses:
            .ds-nav-item carries the row, its hover, and the selected tint keyed
            off aria-current. A plain button rather than the DS Button, whose
            secondary/ghost variants drew the active row as a gray button. */}
        <nav className="ds-nav">
          {(actsAcrossTheFleet ? STAFF_NAV : CUSTOMER_NAV).map(({ id, label, icon: NIcon }) => (
            <button
              key={id}
              type="button"
              className="ds-nav-item"
              data-rail={navCollapsed ? "" : undefined}
              aria-label={navCollapsed ? label : undefined}
              title={navCollapsed ? label : undefined}
              aria-current={active === id ? "page" : undefined}
              onClick={() => onNavigate(id)}
            >
              <NIcon />{navCollapsed ? null : <> <span>{label}</span></>}
            </button>
          ))}
        </nav>
        {/* Gated with the nav above it, and for the same reason rather than a
            weaker one: this is where a CUSTOMER goes to subscribe, upgrade and
            pay. A metricsIQ person has nothing to buy in a customer's
            marketplace, so drawing it on their sidebar would be offering an
            action that is not theirs to take. */}
        {!actsAcrossTheFleet && !navCollapsed && <MarketplaceConsoleLink />}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 items-center justify-between border-b bg-card px-6 py-2">
          <div className="flex items-center gap-3">
            <Avatar className="size-8"><AvatarFallback>{surface.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
            <div className="leading-tight">
              <div className="text-sm font-semibold">{surface}</div>
            </div>
            {/* ADDED (M-W35-36 T5): the switcher, beside the line that says
                whose surface this is — because that line is what it changes.
                Drawn only where there is somewhere else to go, so a customer
                belonging to one customer and holding no role sees the masthead
                exactly as it was. */}
            {elsewhere.length > 0 && (
              <ScopeSwitcher places={elsewhere} onSwitchTo={onSwitchTo} />
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              {/* ADAPTED: same reason as the popover trigger — the DS
                  DropdownMenuTrigger renders the button itself, so the ghost
                  styling comes from the DS `buttonVariants` recipe instead of
                  from a nested <Button>. */}
              <DropdownMenuTrigger className={buttonVariants({ variant: "ghost", className: "gap-2 px-2" })}>
                {/* ADAPTED: the second line was the fixture's role. There is no
                    role on a CustomerMembership to report — see the PR — so it
                    shows which customer this session acts for, which is real,
                    and is the thing a person with two of them needs to see.
                    For somebody who acts for no customer it shows the role they
                    do act under, which is the same question answered from the
                    other shape. */}
                <span className="text-right leading-tight">
                  <span className="block text-sm font-medium">{signedInAs}</span>
                  <span className="block text-xs text-muted-foreground">{actsFor}</span>
                </span>
                <Avatar className="size-8"><AvatarFallback>{avatar}</AvatarFallback></Avatar>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{signedInAs}</div>
                  <div className="text-xs font-normal text-muted-foreground">{actsFor}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* ADAPTED: `onAction` is the React Aria menu-item handler the DS
                    menu is built on, and it closes the menu as part of selecting.

                    Gated for the same reason the nav is: the account modal
                    behind all three of these reads GET /portal/api/account/,
                    which is a customer endpoint and answers a metricsIQ
                    person's token 401 — and it draws the customer their
                    membership names, which there is none of here. Three menu
                    entries that open a dialog saying the service refused is
                    worse than three entries that are not offered. Where a
                    metricsIQ person changes their own password is the
                    server-rendered account surface, and giving them one inside
                    the portal is T5/T6's business, not a stub for this task. */}
                {!actsAcrossTheFleet && (
                  <>
                    <DropdownMenuItem onAction={() => onOpenAccount("profile")}><Settings className="size-4" /> Account settings</DropdownMenuItem>
                    <DropdownMenuItem onAction={() => onOpenAccount("security")}><Lock className="size-4" /> Password &amp; security</DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onAction={onSignOut}><LogOut className="size-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="min-w-0 flex-1">{children}</main>
        <footer className="flex items-center justify-between border-t px-6 py-4">
          <div className="flex items-center gap-3">
            <Logo mark="metricsIQ" alt="metricsIQ" className="h-4" />
            <span className="text-xs text-muted-foreground">Your Agentic AI Transformation Partner</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>© 2026 metricsIQ LLC.</span>
            <a href={LEGAL.privacy} className="hover:text-foreground" target="_blank" rel="noreferrer">Privacy</a>
            <a href={LEGAL.terms} className="hover:text-foreground" target="_blank" rel="noreferrer">Terms</a>
            <a href={LEGAL.cookies} className="hover:text-foreground" target="_blank" rel="noreferrer">Cookie Policy</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
